import { AssortmentBinding, ConnectAssortmentsDialog } from '@retail/member-config/components';
import {
  useCreateAssortment,
  useDeleteAssortment,
  useUpdateAssortmentFields,
} from '@retail/member-config/data-access';
import { DataOwnerConfig, NewAssortmentPost } from '@retail/member-config/types';
import { useToast } from '@retail/hooks';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

interface Props {
  memberForAssortmentConnection: DataOwnerConfig;
  onClose: () => void;
}

const sameLocationSet = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((id, index) => id === sortedB[index]);
};

export function ConnectAssortmentsDialogContainer({
  memberForAssortmentConnection,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();

  const assortments = useMemo(
    () => memberForAssortmentConnection.assortments,
    [memberForAssortmentConnection.assortments]
  );
  const [assortmentBindings, setAssortmentBindings] = useState<AssortmentBinding[]>(
    assortments.map((assortment): AssortmentBinding => {
      return {
        id: crypto.randomUUID(),
        assortmentId: assortment.id,
        title: assortment.title,
        locations: assortment.locations.map((location) => {
          return { id: location.id, name: location.name };
        }),
        isExisting: true,
      };
    })
  );

  const allLocations = useMemo(
    () =>
      memberForAssortmentConnection.locations.map((location) => ({
        id: location.id,
        name: location.name,
      })),
    [memberForAssortmentConnection.locations]
  );

  const { mutateAsync: createAssortmentsAsync } = useCreateAssortment({
    dataOwnerId: memberForAssortmentConnection.id,
  });
  const { mutateAsync: deleteAssortmentAsync } = useDeleteAssortment();
  const { mutateAsync: updateAssortmentFieldsAsync } = useUpdateAssortmentFields();

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    const remainingAssortmentIds = new Set(
      assortmentBindings
        .map((binding) => binding.assortmentId)
        .filter((id): id is number => id !== undefined)
    );

    // Existing assortments removed -> delete
    const deletedAssortmentIds = assortments
      .filter((assortment) => !remainingAssortmentIds.has(assortment.id))
      .map((assortment) => assortment.id);

    // New assortments (no assortment id) -> create
    const newAssortments: NewAssortmentPost[] = assortmentBindings
      .filter((binding) => binding.assortmentId === undefined)
      .map((binding) => ({
        title: binding.title.trim(),
        locations: binding.locations.map((location) => location.id),
      }));

    // Existing assortments whose locations changed -> update locations
    const assortmentsWithChangedLocations = assortmentBindings.filter((binding) => {
      if (binding.assortmentId === undefined) return false;
      const original = assortments.find((assortment) => assortment.id === binding.assortmentId);
      if (!original) return false;
      return !sameLocationSet(
        original.locations.map((location) => location.id),
        binding.locations.map((location) => location.id)
      );
    });

    if (
      !deletedAssortmentIds.length &&
      !newAssortments.length &&
      !assortmentsWithChangedLocations.length
    ) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      //Connect locations first so that any assortment about to be deleted is emptied of its locations
      //— an assortment can only be deleted once it has no connected locations.
      await Promise.all(
        assortmentsWithChangedLocations.map((binding) =>
          updateAssortmentFieldsAsync({
            urlVariables: { assortmentId: binding.assortmentId },
            body: {
              locations: binding.locations.map((location) => location.id),
              updateLocations: true,
            },
          })
        )
      );

      // Now that locations have been reassigned, deletions and creations can run.
      await Promise.all([
        ...deletedAssortmentIds.map((assortmentId) =>
          deleteAssortmentAsync({ urlVariables: assortmentId })
        ),
        ...newAssortments.map((assortment) => createAssortmentsAsync({ body: assortment })),
      ]);
      queryClient.invalidateQueries({ queryKey: ['member-config'] });
      toast.success(t('myAssortment.memberConfig.connectionsAreUpdated'));
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(t('myAssortment.actions.genericActionError'));
    } finally {
      setIsSaving(false);
    }
  }, [
    assortmentBindings,
    assortments,
    createAssortmentsAsync,
    deleteAssortmentAsync,
    onClose,
    queryClient,
    t,
    toast,
    updateAssortmentFieldsAsync,
  ]);

  return (
    <ConnectAssortmentsDialog
      assortmentBindings={assortmentBindings}
      setAssortmentBindings={setAssortmentBindings}
      allLocations={allLocations}
      onSave={handleSave}
      isSaving={isSaving}
      onClose={onClose}
    />
  );
}
